// @ts-check
// planService.js
// Purpose: Handles all CRUD operations and cache logic for plans in Supabase.
// Features: PERMANENT CACHE + INCREMENTAL SYNC for optimal performance and cost efficiency
//
// CACHING STRATEGY OVERVIEW:
// ========================
// Traditional Approach (OLD): Cache expires every 5 minutes → Full refetch of ALL plans
// New Approach (IMPLEMENTED): Permanent cache + incremental sync for new/updated plans only
//
// BENEFITS:
// - Instant loading: Plans always available from cache
// - Cost efficient: Only fetch new/changed plans since last sync
// - Bandwidth friendly: Minimal data transfer
// - Offline resilient: Works with cached data when offline
// - Real-time feel: Background sync keeps data fresh
//
// HOW IT WORKS:
// 1. First load: Full fetch → Store in permanent cache with sync timestamp
// 2. Subsequent loads: Return cached data instantly → Background incremental sync
// 3. Incremental sync: Only fetch plans newer/updated since lastSync timestamp
// 4. New data creation: Add directly to cache (no clearing)
// 5. Fallback: Full refresh if incremental sync fails repeatedly
//
// PLANS SPECIAL CONSIDERATIONS:
// - Plans are typically larger documents with detailed content
// - Updates may include progress tracking, milestone completion
// - Incremental sync uses OR condition: created_at > lastSync OR updated_at > lastSync
//
// Inputs: userId, plan data, cacheManager, supabase

import { supabase } from '../../config/supabase';
import { getAppSessionId, getUuidSessionId } from '../../utils/activityEvents';
// Use centralized cacheManager to avoid duplicate instances. Prefer this path across services.
import logger from '../../utils/logger';
import { cacheManager } from '../cacheManager';
import { PlanSchema } from '../../lib/schemas/plan';
/**
 * Insert a plan row safely, returning the inserted row or error.
 * Uses select().single() to return the created row.
 * @param {Record<string, any>} payload
 * @returns {Promise<{ data: any, error: any }>}
 */

// --- Internal helpers -------------------------------------------------------

/**
 * Normalize cadence payload to ensure valid enum and mirror cadence_type.
{{ ... }}
 * Also strips legacy keys that must not be sent (plan_json, duration_weeks, activities, title).
 * @param {Record<string, any>} input
 */
function normalizePlanWrite(input) {
  const out = { ...(input || {}) };

  // Strip legacy fields that no longer exist in new schema
  delete out.plan_json;
  delete out.activities;
  delete out.cadence_type; // No longer in new schema
  delete out.review_cadence; // No longer in new schema
  delete out.next_review_at; // No longer in new schema
  delete out.last_review_at; // No longer in new schema
  delete out.success_criteria; // No longer in new schema
  delete out.kpis; // No longer in new schema
  delete out.cadence; // No longer in new schema
  delete out.priority; // No longer in new schema
  delete out.owner; // No longer in new schema

  // Map legacy 'goal' field to 'title' if present
  if (out.goal && !out.title) {
    out.title = out.goal;
  }
  delete out.goal; // Remove legacy field

  // Map new plan_tasks to legacy plan_actions for DB compatibility
  if (Array.isArray(out.plan_tasks)) {
    out.plan_actions = out.plan_tasks;
    delete out.plan_tasks;
  }

  // Ensure JSONB fields are arrays with defaults
  if (!Array.isArray(out.risks)) out.risks = [];
  if (!Array.isArray(out.milestones)) out.milestones = [];
  if (!Array.isArray(out.dependencies)) out.dependencies = [];
  if (!Array.isArray(out.assumptions)) out.assumptions = [];
  if (!Array.isArray(out.plan_actions)) out.plan_actions = [];

  // Ensure numeric fields have defaults
  if (typeof out.risk_score !== 'number') out.risk_score = 0;
  if (typeof out.tasks_total !== 'number') out.tasks_total = 0;
  if (typeof out.tasks_done !== 'number') out.tasks_done = 0;

  return out;
}
/**
 * Attempt an insert, and if we hit PGRST204 unknown column, drop that column and retry.
 * Also runs normalizePlanWrite before sending.
 * @param {Record<string, any>} payload
 */
async function safeInsertPlan(payload) {
  let toSend = normalizePlanWrite(payload);
  for (let attempt = 0; attempt < 2; attempt++) {
    const { data, error } = await supabase.from('plans').insert([toSend]).select().maybeSingle();
    if (!error) return { data, error: null };
    const msg = String(error?.message || '');
    if (error?.code === 'PGRST204' && msg.includes("Could not find the '")) {
      const m = msg.match(/Could not find the '([^']+)' column/);
      const col = m && m[1];
      if (col && Object.prototype.hasOwnProperty.call(toSend, col)) {
        delete toSend[col];
        continue;
      }
    }
    return { data: null, error };
  }
  // Last attempt to surface the error or succeed
  const last = await supabase.from('plans').insert([toSend]).select().maybeSingle();
  return last;
}

/**
 * @param {string} userId
 * @param {string} planId
 * @param {Record<string, any>} updates
 */
async function safeUpdatePlan(userId, planId, updates) {
  let toSend = normalizePlanWrite(updates);
  for (let attempt = 0; attempt < 2; attempt++) {
    const { data, error } = await supabase
      .from('plans')
      .update(toSend)
      .eq('id', planId)
      .eq('user_id', userId)
      .select()
      .maybeSingle();
    if (!error) return { data, error: null };
    const msg = String(error?.message || '');
    if (error?.code === 'PGRST204' && msg.includes("Could not find the '")) {
      const m = msg.match(/Could not find the '([^']+)' column/);
      const col = m && m[1];
      if (col && Object.prototype.hasOwnProperty.call(toSend, col)) {
        delete toSend[col];
        continue;
      }
    }
    return { data: null, error };
  }
  const last = await supabase
    .from('plans')
    .update(toSend)
    .eq('id', planId)
    .eq('user_id', userId)
    .select()
    .maybeSingle();
  return last;
}

/**
 * @param {string} userId
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal }} [options]
 */
export async function getPlans(userId, { forceRefresh = false, signal } = {}) {
  if (!userId) {
    logger.warn('⚠️ planService.getPlans: Missing userId');
    return [];
  }
  const cacheKey = 'plans';
  logger.info('📋 planService.getPlans: Fetching plans for user:', userId, { forceRefresh });

  // STEP 1: PERMANENT CACHE CHECK
  // =============================
  // Prefer cached data for instant UI, but avoid the "empty baseline" trap.
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    const cached = cacheManager.get(cacheKey, userId);
    const hasBaseline = Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId));
    if (cached && Array.isArray(cached)) {
      // Filter cached to current rows only
      const filteredCached = cached.filter((p) => Boolean(p && p.is_current === true));
      if (filteredCached.length > 0 && hasBaseline) {
        logger.debug('✅ planService.getPlans: Returning cached plans:', cached.length);
        return filteredCached;
      }

      // If cache exists but is empty or baseline missing, perform FULL FETCH now
      logger.info(
        '⚠️ planService.getPlans: Cached plans are empty or missing baseline. Performing full fetch. { cachedLength:',
        cached.length,
        ', hasBaseline:',
        hasBaseline,
        ' }',
      );
      try {
        const fresh = await performFullFetch(userId, cacheKey, signal);
        return Array.isArray(fresh) ? fresh : [];
      } catch (e) {
        logger.error('❌ planService.getPlans: Full fetch after empty cache failed:', e, {
          hasBaseline,
        });
        return [];
      }
    }
  }

  // STEP 2: FULL FETCH (NO CACHE OR FORCE REFRESH)
  // ==============================================
  // No cache exists or user explicitly requested fresh data
  // Perform full fetch to establish cache baseline
  try {
    logger.info('🔄 planService.getPlans: Performing full fetch (no cache or force refresh)');
    return await performFullFetch(userId, cacheKey, signal);
  } catch (error) {
    logger.error('❌ planService.getPlans: Full fetch failed:', error, {
      hasBaseline: Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId)),
    });

    // FALLBACK STRATEGY
    // ================
    // If fresh fetch fails, return cached data if available (even if potentially stale)
    // This ensures app remains functional even with network issues
    const fallbackCache = cacheManager.get(cacheKey, userId);
    if (Array.isArray(fallbackCache)) {
      logger.info('🔄 planService.getPlans: Returning fallback cached data:', fallbackCache.length);
      return fallbackCache;
    }

    // No cache available, return empty array
    logger.warn('⚠️ planService.getPlans: No cache available, returning empty array');
    return [];
  }
}

/**
 * Perform full fetch of all plans for a user
 *
 * FULL FETCH STRATEGY:
 * ===================
 * This function is used when:
 * 1. No cache exists (first time loading plans)
 * 2. User explicitly requests fresh data (forceRefresh=true)
 * 3. Incremental sync has failed multiple times (fallback)
 *
 * ESTABLISHES CACHE BASELINE:
 * - Fetches ALL plans for the user
 * - Stores them in permanent cache
 * - Sets sync timestamp for future incremental syncs
 * - This becomes the foundation for all future incremental updates
 *
 * @param {string} userId - User ID
 * @param {string} cacheKey - Cache key for plans
 * @param {(AbortSignal|undefined)} signal - Abort signal (optional)
 * @returns {Promise<any[]>} Array of plans (from DB)
 */
async function performFullFetch(userId, cacheKey, signal) {
  logger.info('🔄 planService.performFullFetch: Starting full fetch from Supabase');
  // simplified: no cooldown bookkeeping

  // FULL FETCH QUERY
  // ===============
  // Fetch ALL plans for this user (no filtering)
  // This establishes the complete baseline for future incremental syncs
  let fullQuery = supabase
    .from('plans')
    // Filter directly by the new plans.user_id column (schema updated)
    .select('*')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .eq('is_current', true)
    .order('created_at', { ascending: false });
  if (signal) fullQuery = fullQuery.abortSignal(signal);
  const { data, error } = await fullQuery;

  if (error) {
    logger.error('❌ planService.performFullFetch: Supabase error:', error);
    throw error;
  }

  logger.info(
    '✅ planService.performFullFetch: Fetched from Supabase:',
    data?.length || 0,
    'plans',
  );

  // ESTABLISH CACHE BASELINE
  // =======================
  // Store ALL plans in permanent cache with current timestamp
  // This timestamp becomes the reference point for future incremental syncs
  // Future syncs will only fetch plans newer/updated than this timestamp
  const syncTimestamp = new Date().toISOString();
  // Validate with Zod (non-blocking). Keep originals to avoid breaking UI.
  const validated = Array.isArray(data)
    ? data.map((row) => {
        const res = PlanSchema.safeParse(row);
        if (!res.success) {
          logger.warn('planService.performFullFetch: Plan validation failed (non-blocking)', {
            id: /** @type {any} */ (row)?.id,
            issues: res.error.issues?.slice(0, 3),
          });
          return row;
        }
        return res.data;
      })
    : [];
  cacheManager.mergeIncremental(cacheKey, validated, userId, syncTimestamp);

  logger.debug(
    '✅ planService.performFullFetch: Cached',
    data?.length || 0,
    'plans with sync timestamp:',
    syncTimestamp,
  );
  return data || [];
}

/**
 * Create a new plan and add it to permanent cache
 * @param {string} userId - User ID
 * @param {object} planData - Plan data to create
 * @returns {Promise<any>} Created plan
 */
export async function createPlan(userId, planData) {
  logger.info('📋 planService.createPlan: Creating new plan for user:', userId);

  // Normalize payload through normalizePlanWrite
  /** @type {Record<string, any>} */
  const payload = normalizePlanWrite(planData || {});

  // Attempt insert with fallback stripping of unknown columns (PGRST204)
  const { data, error } = await safeInsertPlan({ ...payload, user_id: userId });
  if (error) {
    logger.error('❌ planService.createPlan: Supabase error:', error);
    throw error;
  }

  logger.info('✅ planService.createPlan: Created plan:', data.id);

  // ADD TO PERMANENT CACHE
  // =====================
  // Add new plan directly to cache instead of clearing it
  // This maintains instant loading while keeping data fresh
  const cacheKey = 'plans';
  const syncTimestamp = new Date().toISOString();
  {
    const res = PlanSchema.safeParse(data);
    if (!res.success) {
      logger.warn('planService.createPlan: Plan validation failed (non-blocking)', {
        id: /** @type {any} */ (data)?.id,
        issues: res.error.issues?.slice(0, 3),
      });
      cacheManager.mergeIncremental(cacheKey, [data], userId, syncTimestamp);
    } else {
      cacheManager.mergeIncremental(cacheKey, [res.data], userId, syncTimestamp);
    }
  }

  logger.debug('✅ planService.createPlan: Added new plan to permanent cache');
  // Best-effort analytics: record plan_created
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'plan',
      entity_id: data.id,
      title: /** @type {any} */ (data)?.title,
      linked_goal_id: /** @type {any} */ (data)?.linked_goal_id,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'plan_created',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] plan_created insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] plan_created exception:', err);
  }

  return data;
}

/**
 * Create a new version of an existing plan (v2+)
 *
 * Behavior per docs/data/DataContracts.md:
 * - Set: plan_lineage (from prev row lineage or id), user_id, linked_goal_id (usually same),
 *   and content fields being changed. Optionally plan_change_notes.
 * - Leave unset: plan_version, previous_plan_version, is_current (handled by triggers).
 * - Unspecified content fields are copied forward from the previous version so the new row
 *   is a full updated copy of the plan.
 *
 * @param {string} userId
 * @param {any} prevPlan - The current (is_current) plan row being versioned
 * @param {Record<string, any>} updates - The fields changed by the user
 * @returns {Promise<any>} The newly created plan row (new version)
 */
export async function createPlanVersion(userId, prevPlan, updates) {
  logger.info('📋 planService.createPlanVersion: Start', { userId, prevId: prevPlan?.id });
  if (!userId || !prevPlan?.id) throw new Error('Missing userId or previous plan row');

  /** @type {string[]} */
  // Only include first-class content fields from new schema.
  const contentKeys = [
    'title',
    'status',
    'health',
    'linked_goal_id',
    'linked_goal_title',
    'start_date',
    'end_date',
    'duration_weeks',
    'milestones',
    'dependencies',
    'assumptions',
    'risks',
    // prefer new key in updates; normalizePlanWrite maps it for DB
    'plan_tasks',
    'plan_change_notes',
  ];

  /** @type {Record<string, any>} */
  const merged = {};
  for (const k of contentKeys) {
    if (Object.prototype.hasOwnProperty.call(prevPlan, k)) merged[k] = prevPlan[k];
  }
  for (const [k, v] of Object.entries(updates || {})) merged[k] = v;

  const insertPayload = {
    user_id: userId,
    plan_lineage: prevPlan.plan_lineage || prevPlan.id,
    ...merged,
  };

  const { data, error } = await safeInsertPlan(insertPayload);
  if (error) {
    logger.error('❌ planService.createPlanVersion: Supabase error:', error);
    throw error;
  }

  const cacheKey = 'plans';
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, [data], userId, syncTimestamp);
  logger.info('✅ planService.createPlanVersion: Created new version', { id: data.id });
  return data;
}
/**
 * Update an existing plan and refresh it in permanent cache
 * @param {string} userId - User ID
 * @param {string} planId - Plan ID to update
 * @param {object} updates - Updates to apply
 * @returns {Promise<any>} Updated plan
 */
export async function updatePlan(userId, planId, updates) {
  logger.info('📋 planService.updatePlan: Updating plan:', planId, 'for user:', userId);

  // Normalize updates through normalizePlanWrite
  /** @type {Record<string, any>} */
  const safeUpdates = normalizePlanWrite(updates || {});

  const { data, error } = await safeUpdatePlan(userId, planId, safeUpdates);
  if (error) {
    logger.error('❌ planService.updatePlan: Supabase error:', error);
    throw error;
  }
  if (!data) {
    // No row returned means the filter matched zero rows (id/user mismatch or RLS)
    const err = new Error('Update failed: no matching plan found or permission denied');
    logger.warn('⚠️ planService.updatePlan: No row returned for update', { userId, planId });
    throw err;
  }

  logger.info('✅ planService.updatePlan: Updated plan:', data.id);

  // UPDATE IN PERMANENT CACHE
  // ========================
  // Merge updated plan into cache (replaces existing by ID)
  // This ensures cache reflects the latest changes immediately
  // Important for plans due to progress updates and content changes
  const cacheKey = 'plans';
  const syncTimestamp = new Date().toISOString();
  cacheManager.mergeIncremental(cacheKey, [data], userId, syncTimestamp);

  logger.debug('✅ planService.updatePlan: Updated plan in permanent cache');
  // Best-effort analytics: record plan_updated
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'plan',
      entity_id: data.id,
      title: /** @type {any} */ (data)?.title,
      linked_goal_id: /** @type {any} */ (data)?.linked_goal_id,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'plan_updated',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] plan_updated insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] plan_updated exception:', err);
  }
  return data;
}

/**
 * Delete a plan and remove it from permanent cache
 * @param {string} userId - User ID
 * @param {string} planId - Plan ID to delete
 * @returns {Promise<boolean>} Success status
 */
export async function deletePlan(userId, planId) {
  logger.info('📋 planService.deletePlan: Deleting plan:', planId, 'for user:', userId);

  // Soft delete: set deleted_at to current timestamp rather than hard delete
  const deletedAt = new Date().toISOString();
  const { data: updated, error } = await supabase
    .from('plans')
    .update({ deleted_at: deletedAt })
    .eq('id', planId)
    .eq('user_id', userId)
    .select('id, deleted_at');

  if (error) {
    logger.error('❌ planService.deletePlan: Supabase error:', error);
    throw error;
  }
  if (!updated || updated.length === 0) {
    logger.warn(
      '⚠️ planService.deletePlan: No rows updated. Possible user_id mismatch or RLS policy.',
      { userId, planId },
    );
    throw new Error('Delete failed: no matching plan found or permission denied');
  }
  logger.info('✅ planService.deletePlan: Deleted plan from database', updated);

  logger.info('✅ planService.deletePlan: Deleted plan from database');

  // REMOVE FROM PERMANENT CACHE (item-level)
  // =======================================
  const cacheKey = 'plans';
  try {
    const prev = /** @type {any[]|null} */ (cacheManager.get(cacheKey, userId));
    const next = Array.isArray(prev) ? prev.filter((p) => String(p?.id) !== String(planId)) : [];
    cacheManager.set(cacheKey, next, userId);
    logger.debug('✅ planService.deletePlan: Removed plan from cache (item-level)');
  } catch (e) {
    logger.warn('planService.deletePlan: Failed to remove from cache (non-blocking):', e);
  }
  // Best-effort analytics: record plan_deleted
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'plan',
      entity_id: planId,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'plan_deleted',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] plan_deleted insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] plan_deleted exception:', err);
  }
  return true;
}
